// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { Mutation } from 'react-apollo';

import Logging from '@ta/common.logging';
import classNames from '@ta/common.classnames';
import { Avatar } from '@ta/styleguide.avatar';
import { ConfirmationPopover } from '@ta/social.common-popover';
import { localize } from '@ta/common.i18n';
import { ToastConsumer, ToastItem } from '@ta/core-ui.toast';
import { NativeBridge } from '@ta/common.webview';
import { RouteConsumer } from '@ta/common.routing';
import { featureIsEnabled } from '@ta/common.features';
import * as Types from '@ta/trips.trip-types';
import { getCollabPermissions, getTripPermissions } from '@ta/trips.trip-util';
import { ShareTripPopup } from './ShareTripPopup';

import styles from './CollaboratorPanel.less';
import RemoveCollaborator from './RemoveCollaborator.gql';
import AddFriendIcon from './AddFriendIcon';

type RemoveCollabFunc = Types.Collaborator => void;

type MenuProps = {|
  /** The trip object to get the collaborators from */
  trip: Types.Trip,
  /** Refetch any queries after the mutation has finished */
  refetchQueries?: { query: any, variables?: {} }[],
  /** Do not show any controls, even if the user has permission. This is useful on the homefeed
   * when we just want to show a list of collaborators and not let anybody edit them.
   */
  readOnly: boolean,

  /** optional callback for when the remove menu is shown */
  onRemoveShow?: Types.Collaborator => void,

  onAddCollaboratorClick?: () => void,

  /** optional callback for when the user is removed */
  onRemove?: Types.Collaborator => void,
  /** Whether to lazy load the avatars or not */
  avatarNotLazy: boolean,
|};

type InternalMenuProps = {|
  ...MenuProps,
  /** Are we a native webview */
  isNativeWebview: boolean,
  loggedInUserId: string,
|};

type CollabProps = {|
  /** The user we're displaying */
  user: Types.Member,
  /** Node to display to give the user some context */
  contextNode: ?React.Node,
  /** Whether to lazyload the avatars or not */
  avatarNotLazy: boolean,
|};

type AddFriendProps = {|
  /** The trip object to get the collaborators from */
  trip: Types.Trip,
  onClick?: () => void,
|};

/**
 * Displays one line of the collaborator menu. Will handle what controls are displayed to the user.
 */
const UserDisplay = ({ user, contextNode, avatarNotLazy }: CollabProps): React.Node => {
  const profileLink = `/Profile/${user.username}`;
  const avatar = (
    <Avatar
      notLazy={avatarNotLazy}
      size="default"
      href={profileLink}
      withBorder
      photo={user.avatar}
      mobileTarget="_self"
    />
  );

  return (
    <div className={styles.collabItem}>
      {avatar}
      <a href={profileLink} className={classNames(styles.displayName, 'ui_link')}>
        {user.displayName}
      </a>
      {contextNode && <div className={styles.context}>{contextNode}</div>}
    </div>
  );
};

const AddFriend = ({ trip, onClick }: AddFriendProps): React.Node => (
  // TODO: get translations
  <ShareTripPopup shiftArrow={7} trip={trip} shareType="WRITE">
    {(target, openPopup) => {
      const handleOnClick = () => {
        openPopup();
        if (onClick) {
          onClick();
        }
      };

      return (
        <div className={classNames(styles.collabItem, styles.collabHeader)}>
          <AddFriendIcon onClick={handleOnClick} targetRef={target} />

          <div onClick={handleOnClick} className={classNames(styles.addFriendsText, 'ui_link')}>
            {localize('trips_add_CTA') /* Add */}
          </div>
        </div>
      );
    }}
  </ShareTripPopup>
);

/**
 * Shows a list of users that belong to the trip.
 * With the correct permissions you can add/remove yourself or other users.
 */
class CollaboratorPanel extends React.PureComponent<InternalMenuProps> {
  static defaultProps = {
    isNativeWebview: false,
    refetchQueries: [],
    readOnly: false,
    onRemoveShow: undefined,
    onRemove: undefined,
    avatarNotLazy: false,
  };

  /**
   * Return the proper context for a user line
   */
  getContextNode = (
    trip: Types.Trip,
    collaborator: Types.Collaborator,
    removeCollab: RemoveCollabFunc,
  ): React.Node => {
    // we have no beta value for this display, so return false if we're not using the collab feature.
    const collabPermissions = getCollabPermissions(collaborator, false);

    if (!collabPermissions?.canRemove) {
      return null;
    }

    return this.renderConfirmationAndContent(collaborator, removeCollab);
  };

  /**
   * Render a collaborator removal confirmation with the specified content
   * @param content
   * @param description
   */
  renderConfirmationAndContent = (
    collaborator: Types.Collaborator,
    removeCollab: RemoveCollabFunc,
  ): React.Node => {
    let header;
    let description;
    let cta;
    const { user = {} } = collaborator;
    if (user.isMe) {
      header = localize('trips_leave_trip_modal_header'); // Leave Trip?
      description = localize('trips_leave_trip_modal_body'); // Are you sure you want to leave this Trip? You will no longer be able to add or edit content.
      cta = localize('trips_leave_CTA'); // Leave
    } else {
      header = localize('trips_remove_collaborator_modal_header'); // Remove Collaborator?
      description = localize('trips_remove_collaborator_modal_body'); // Are you sure you want to remove this collaborator? They will no longer be able to add or edit content.
      cta = localize('trips_remove_CTA'); // Remove
    }

    const submitLabel = cta;
    const cancelLabel = localize('stat_modal_cancel'); /* Cancel */

    const confirmCallback = () => removeCollab(collaborator);

    // override the confirmation open function with this if necessary
    let nativeOpen = null;

    if (this.props.isNativeWebview) {
      nativeOpen = () =>
        NativeBridge.showNativeOverflowMenu({
          treatment: 'alert',
          title: header,
          subtitle: description,
          items: [
            {
              title: cancelLabel,
              tapCallback: () => {
                // do nothing, menu will auto-close on tap
              },
            },
            {
              preferred: true,
              title: submitLabel,
              destructive: true,
              tapCallback: confirmCallback,
            },
          ],
        });
    }

    return (
      <ConfirmationPopover
        header={header}
        description={description}
        cancel={cancelLabel}
        confirm={submitLabel}
        above
        alignEdge="right"
        shiftY={30}
        onConfirm={confirmCallback}
        onCancel={null}
      >
        {(target, webOpen) => (
          <div
            ref={target}
            onClick={() => {
              (nativeOpen || webOpen)();
              if (this.props.onRemoveShow) {
                this.props.onRemoveShow(collaborator);
              }
            }}
            className="ui_link"
          >
            {cta}
          </div>
        )}
      </ConfirmationPopover>
    );
  };

  renderPanel(removeCollab: RemoveCollabFunc) {
    const { props } = this;
    const { collaborators } = props.trip;

    const ownerContext = <div className={styles.owner}>{localize('trips_owner') /* Owner */}</div>;

    // we have no beta value for this display, so return false if we're not using the collab feature.
    const tripPermissions = getTripPermissions(props.trip, false);

    return (
      <div className={styles.collabWrap}>
        {tripPermissions?.canAddCollaborators && !props.readOnly && (
          <AddFriend trip={props.trip} onClick={props.onAddCollaboratorClick} />
        )}
        <div className={styles.collabList}>
          {props.trip.owner && (
            <UserDisplay
              user={props.trip.owner}
              contextNode={ownerContext}
              avatarNotLazy={props.avatarNotLazy}
            />
          )}

          {collaborators &&
            collaborators.map(
              collab =>
                collab.user && (
                  <UserDisplay
                    key={collab.userId}
                    user={collab.user}
                    avatarNotLazy={props.avatarNotLazy}
                    contextNode={
                      !props.readOnly && this.getContextNode(props.trip, collab, removeCollab)
                    }
                  />
                ),
            )}
        </div>
      </div>
    );
  }

  render() {
    if (!featureIsEnabled('trips_2018_collab')) {
      return null;
    }

    return (
      <RouteConsumer>
        {({ navigate }) => (
          <ToastConsumer>
            {launchToast => (
              <Mutation
                /* $FlowFixGraphql (WP-1357) */
                mutation={RemoveCollaborator}
                refetchQueries={this.props.refetchQueries}
                awaitRefetchQueries
                onError={error => Logging.error(error)}
              >
                {mutate => {
                  const removeCollab = (collaborator: Types.Collaborator) => {
                    if (this.props.onRemove) {
                      this.props.onRemove(collaborator);
                    }

                    mutate({
                      variables: {
                        collaboratorId: collaborator.id,
                      },
                    }).then(() => {
                      // If the trip is not public and the collaborator removed themselves
                      // then navigate to the /trips page
                      const collabSelfRemove = collaborator.user && collaborator.user.isMe;
                      if (this.props.trip.status !== 'PUBLIC' && collabSelfRemove) {
                        navigate({ page: 'Trips' });
                        return;
                      }

                      const name =
                        (collaborator.user && collaborator.user.displayName) ||
                        localize('trips_unknown_user'); // Unknown user
                      launchToast({
                        node: (
                          <ToastItem>
                            {/* {UserName} was successfully removed from the Trip */}
                            {localize('trips_collaborator_removed_confirm', { UserName: name })}
                          </ToastItem>
                        ),
                      });
                    });
                  };

                  return this.renderPanel(removeCollab);
                }}
              </Mutation>
            )}
          </ToastConsumer>
        )}
      </RouteConsumer>
    );
  }
}

export default connect<InternalMenuProps, MenuProps, _, _, _, _>(state => ({
  loggedInUserId: state.auth.loggedInUserId,
  isNativeWebview: state.meta.isNativeWebview,
}))(CollaboratorPanel);
